import { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { useTransition, animated } from "@react-spring/web";
import CloseIcon from "@mui/icons-material/CloseRounded";
import useLocalization from "../../../../hooks/localization";
import { closeAccessibilityMenu } from "../../../../redux/actions/chatActions";
import useFocusTrap from "../../../common/focusTrap";
import TextCustomizationSection from "./textCustomization";
import LanguagePreferencesSection from "./languagPreferences";
import { Tooltip } from "@mui/material";

const MenuOverlayContainer = styled(animated.div)`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  bottom: 0;
  right: 0;
  background-color: #fff;
  overflow: hidden;
  user-select: none;
  transform-origin: bottom right;
  z-index: 100;
`;

const OverlayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.veryLightGrey};
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTitle = styled.h2`
  font-size: ${({ theme }) => theme.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.fontColor};
  margin: 0;
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }

`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0.5rem;
  color: ${({ theme }) => theme.darkGrey};
  background-color: transparent;
  font-size: ${({ theme }) => theme.xl};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.veryLightGrey};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.primaryColor};
    outline-offset: 2px;
  }
`;

const StickyPreviewContainer = styled.div`
  position: sticky;
  top: 65px;
  z-index: 9;
  padding: 1.25rem 1.5rem;
  margin: 1.25rem 1.5rem 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.veryLightGrey};
`;

const PreviewLabel = styled.h3`
  font-size: ${({ theme }) => theme.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.fontColor};
  margin-bottom: 0.75rem;
`;

const PreviewText = styled.p`
  color: ${({ theme }) => theme.fontColor};
  margin: 0;
  transition: all 0.3s ease;
  font-family: var(--a11y-font-family);
  font-size: var(--a11y-font-size);
  line-height: var(--a11y-line-height);
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem 1.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const Divider = styled.hr`
  margin: 2rem 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.veryLightGrey};
`;

function AccessibilityOverlay({ showNotification }) {
  const openMenu = useSelector((state) => state?.chat?.openAccessibilityMenu);
  const dispatch = useDispatch();
  const MenuRef = useRef(null);
  const accessibilityTitle = useRef(null);

  const tooltips = useSelector(
    (state) => state?.org?.customizations?.tooltips?.replyBar
  );
  const { localizeTooltips } = useLocalization();
  const localizedTooltips = localizeTooltips(tooltips);
  const assetUrls = useSelector((state) => state?.org?.assetUrls);

  const MenuTransition = useTransition(openMenu, {
    from: { scale: 0.33, opacity: 0.25, borderRadius: "100% 0 0 0" },
    enter: [
      { scale: 1, opacity: 0.67, borderRadius: "100% 0 0 0" },
      { scale: 1, opacity: 1, borderRadius: "0 0 0 0" }
    ],
    leave: { scale: 0, opacity: 0, borderRadius: "100% 0 0 0" },
    config: { duration: 200 } 
  });

  const closeMenu = () => {
    dispatch(closeAccessibilityMenu());
    document.getElementById("accessibility-icon")?.focus();
  };

  const onMenuKeyDown = useFocusTrap(MenuRef, closeMenu);

  const setUpOverlayFocus = () => {
    if (!(openMenu && MenuRef.current)) return;

    const current = MenuRef.current;
    const accTitle = accessibilityTitle.current;
    accTitle.focus();
    current.addEventListener("keydown", onMenuKeyDown);

    return () => {
      current.removeEventListener("keydown", onMenuKeyDown);
    };
  };

  useEffect(setUpOverlayFocus, [openMenu]);

  const [textSize, setTextSize] = useState(16);
  const [spacing, setSpacing] = useState(1.5);
  
  const defaultFontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  const [font, setFont] = useState(defaultFontStack);

  const loadDynamicFont = async () => {
    if (!assetUrls?.fonts) return;
    const url = `${assetUrls.fonts}/Open-Dyslexic.woff2`;

    try {
      const font = new FontFace("OpenDyslexic", `url(${url})`);
      const loadedFont = await font.load();
      document.fonts.add(loadedFont);
    } catch (error) {
      console.error("Font loading failed", error);
    }
  };

    
  useEffect(() => {
    loadDynamicFont();
  }, [assetUrls?.fonts]);

  return (
    <>
      {MenuTransition(({ opacity, ...rest }, displayOverlay) => (
        displayOverlay && (
          <MenuOverlayContainer
            style={{ opacity, ...rest }}
            ref={MenuRef}
            onKeyDown={onMenuKeyDown}
            tabIndex={0}
            id="accessibility-menu"
          >
            <OverlayHeader>
              <HeaderTitle tabIndex={-1} ref={accessibilityTitle}>
                Accessibility Settings
              </HeaderTitle>
              <Tooltip title={localizedTooltips?.closeMenu} placement="top-end">
                <IconButton 
                  onClick={closeMenu}
                  aria-label={localizedTooltips?.closeMenu}
                >
                  <CloseIcon sx={{ fontSize: 'inherit', color: 'inherit' }} />
                </IconButton>
              </Tooltip>
            </OverlayHeader>

            <StickyPreviewContainer>
              <PreviewLabel>Preview</PreviewLabel>
              <PreviewText>
                The quick brown fox jumps over the lazy dog. These settings apply globally.
              </PreviewText>
            </StickyPreviewContainer>

            <ScrollableContent>
              <TextCustomizationSection 
                assetUrls={assetUrls}
                textSize={textSize}
                setTextSize={setTextSize}
                spacing={spacing}
                setSpacing={setSpacing}
                font={font}
                setFont={setFont}
              />


              <Divider />

              <LanguagePreferencesSection showNotification={showNotification} />
            </ScrollableContent>
          </MenuOverlayContainer>
        )
      ))}
    </>
  );
}

export default AccessibilityOverlay;
